// code-guardian-ignore-duplicates: shared presentational scaffolding (bulk-upload modal from the design system); employee-custom-fields twin of BulkUploadModal
import { useState } from 'react';
import { Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import Modal from './Modal';
import { employeeCustomFieldsAPI } from '../services/api';
import { downloadXlsx } from '../utils/download';
import { UploadErrorBanner } from './UploadErrorBanner';
import FileDropZone from './FileDropZone';

interface BulkUploadResult {
  updated: number;
  failed: number;
  errors: { row: number; employeeCode: string; error: string }[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: number;
}

export default function EmployeeCustomFieldsBulkUploadModal({ isOpen, onClose, onSuccess, projectId }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelected = (selectedFile: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError('Please upload a valid Excel file (.xlsx, .xls) or CSV file');
      setFile(null);
      return;
    }
    setFile(selectedFile);
    setError(null);
    setUploadResult(null);
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    setError(null);
    try {
      // Project-scoped: the sheet carries a column per custom field this project has
      // defined, so the same template differs between projects.
      const response = await employeeCustomFieldsAPI.downloadBulkTemplate(projectId);
      downloadXlsx('employee_custom_fields_template.xlsx', response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download template');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = (e.target?.result as string).split(',')[1];
          const response = await employeeCustomFieldsAPI.bulkUpload(projectId, base64Data);

          if (response.data.status === 'success') {
            setUploadResult({
              updated: response.data.updated ?? 0,
              failed: response.data.failed ?? 0,
              errors: response.data.errors ?? [],
            });
            if ((response.data.updated ?? 0) > 0) {
              onSuccess();
            }
          } else {
            setError(response.data.message || 'Upload failed');
          }
        } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to upload file');
        } finally {
          setIsUploading(false);
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setError('Failed to process file');
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk Upload Employee Custom Fields"
      size="lg"
      footer={
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={handleClose}
          >
            {uploadResult ? 'Close' : 'Cancel'}
          </button>
          {!uploadResult && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || isUploading}
              // text-white belongs to the enabled branch only: left on the base it also sat
              // on the disabled button beside text-gray-500, and Tailwind emits text-white
              // later, so the label stayed white on the grey button.
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                file && !isUploading
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'cursor-not-allowed bg-gray-300 text-gray-500'
              }`}
            >
              {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Download Template Section */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900">Download Template</h4>
              <p className="mt-1 text-sm text-blue-700">
                Download the template to see the columns. Fill in the Employee Code and each field's
                value, then upload the file. Only the columns you fill are changed.
              </p>
              <button
                onClick={handleDownloadTemplate}
                disabled={isDownloading}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {isDownloading ? 'Downloading...' : 'Download Template'}
              </button>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        {!uploadResult && (
          <FileDropZone
            inputId="employee-cf-bulk-upload-file"
            file={file}
            onFile={handleFileSelected}
            onClear={() => setFile(null)}
          />
        )}

        {/* Error Message */}
        {error && <UploadErrorBanner message={error} />}

        {/* Upload Results */}
        {uploadResult && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                <CheckCircle className="mx-auto h-6 w-6 text-green-600" />
                <p className="mt-2 text-2xl font-bold text-green-700">{uploadResult.updated}</p>
                <p className="text-xs text-green-600">Employees Updated</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                <AlertCircle className="mx-auto h-6 w-6 text-red-600" />
                <p className="mt-2 text-2xl font-bold text-red-700">{uploadResult.failed}</p>
                <p className="text-xs text-red-600">Failed</p>
              </div>
            </div>

            {uploadResult.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <h4 className="text-sm font-semibold text-red-800">Errors</h4>
                <div className="mt-2 max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-red-200">
                        <th className="py-1 text-left text-red-700">Row</th>
                        <th className="py-1 text-left text-red-700">Employee Code</th>
                        <th className="py-1 text-left text-red-700">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.errors.map((err, idx) => (
                        <tr key={idx} className="border-b border-red-100">
                          <td className="py-1 text-red-600">{err.row}</td>
                          <td className="py-1 text-red-600">{err.employeeCode}</td>
                          <td className="py-1 text-red-600">{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        {!uploadResult && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-semibold text-gray-900">Instructions</h4>
            <ul className="mt-2 space-y-1 text-xs text-gray-600">
              <li>• <strong>Employee Code</strong> is required on every row; blank-code rows are skipped</li>
              <li>• Only the custom-field columns you fill in are updated — others are left as they are</li>
              <li>• For a multi-select field, separate several values with a pipe ( | )</li>
              <li>• Download the template to see this project's fields and their allowed values</li>
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
